import { PlaybackVideoJsPlayer } from '@app/core/video-player/PlaybackVideoJsPlayer';
import { VideoJsPlayerProps } from '@app/core/video-player/video-js/VideoJsPlayer';
import './video-js-audio-skin.css';

/** Default (medium) height of the audio Video.js strip. */
export const VIDEO_JS_AUDIO_PLAYER_HEIGHT = 56;

/** Large strip height: bigger play button and type. */
export const VIDEO_JS_AUDIO_PLAYER_HEIGHT_LARGE = 72;

export const VIDEO_JS_AUDIO_PLAYER_HEIGHTS = {
  medium: VIDEO_JS_AUDIO_PLAYER_HEIGHT,
  large: VIDEO_JS_AUDIO_PLAYER_HEIGHT_LARGE,
} as const;

export type VideoJsAudioPlayerSize = keyof typeof VIDEO_JS_AUDIO_PLAYER_HEIGHTS;

const DEFAULT_OPTIONS: NonNullable<VideoJsPlayerProps['options']> = {
  inactivityTimeout: 0,
  /** Default `fluid` would reserve 16:9 height; audio-only only needs the control strip. */
  fluid: false,
  width: '100%',
  height: VIDEO_JS_AUDIO_PLAYER_HEIGHT,
  playbackRates: [0.5, 1, 1.5, 2],
  controlBar: {
    fullscreenToggle: false,
    pictureInPictureToggle: false,
    remainingTimeDisplay: false,
    volumePanel: false,
    currentTimeDisplay: true,
    durationDisplay: true,
  },
};

export type VideoJsAudioPlayerProps = Omit<VideoJsPlayerProps, 'src'> & {
  /** Visual density of the control strip. Default `medium`. */
  size?: VideoJsAudioPlayerSize;
};

/**
 * Video.js player preset for audio-only playback, rendered as a single control strip
 * (play, elapsed / duration, seek bar, speed) styled by `video-js-audio-skin.css`.
 * `src` is supplied by the surrounding playback provider.
 */
export function VideoJsAudioPlayer({
  options,
  size = 'medium',
  playerClasses,
  ...rest
}: VideoJsAudioPlayerProps) {
  const height = VIDEO_JS_AUDIO_PLAYER_HEIGHTS[size];

  return (
    <PlaybackVideoJsPlayer
      playerClasses={[
        'vjs-audio',
        ...(size === 'large' ? ['vjs-audio-large'] : []),
        ...(playerClasses ?? []),
      ]}
      sx={{
        flex: '0 0 auto',
        width: '100%',
        /** The strip is shorter than the speed menu popup, which must escape the player box. */
        overflow: 'visible',
      }}
      options={{
        ...DEFAULT_OPTIONS,
        ...options,
        height: (options?.height as number | undefined) ?? height,
      }}
      {...rest}
    />
  );
}
